import { TCData } from '@mbrtargeting/metatag-cmp-types';
import { TargetingSettings } from '@mbrtargeting/metatag-config-types';
import { getBrowserDetails, getViewportDimensions, isNumber, loadScript, selectRange, toRanges } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onPhase, Phase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectAdserverGamSettings, selectTargetingPixels, selectTargetingSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { CONFIG_RESOLVER, CONSENT_CACHE } from '../../../loader/token.js';
import { evaluateConsent } from '../../../utils/consent-helper.js';
import { sessionId$ } from '../../observables/session-id.js';
import { GLOBAL_TARGETING, STORAGE_HELPER } from '../../token.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { StorageConsent, StorageHelper, StorageType } from '../storage/storage-helper.js';

const log = logger.logGroup({ prefix: 'GenericTargeting' });

@injectionTarget()
export class GenericTargeting {

    @inject(GLOBAL_TARGETING) private globalTargeting!: GlobalTargeting;
    @inject(STORAGE_HELPER) private storageHelper!: StorageHelper;
    @inject(CONSENT_CACHE) private consentCache!: ConsentCache;
    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;

    private settings: TargetingSettings;

    constructor() {
        this.settings = this.configResolver.get(selectTargetingSettings());
    }

    @onPhase({ phase: Phase.TARGETING })
    public initialSetup(): void {
        this.globalTargeting.setKeyValues({
            tagm: ['mt', 'mt2'], //some campaigns are booked unto tagm=mt, which is why we still use "mt" value
        });
        if (this.settings?.ciStatus) {
            this.globalTargeting.setValues('ci', ['1']);
        }
    }

    @onPhase({ phase: Phase.TARGETING })
    public async checkForInSkinBlock(): Promise<void> {
        const isUnwantedBrowser = () => {
            const reg_safari = /^(?=.*(Safari))(?!.*(Chrome|CriOS)).*$/igm;
            const reg_chrome = /(?!.*OPR).(?=.*Safari)((Chrome|CriOS)[\/\s](\d{1,}([\.][\.A-Za-z0-9]{1,})))/igm;
            const reg_fb = /FBAV\//igm;
            return !(reg_safari.test(navigator.userAgent) || reg_fb.test(navigator.userAgent) || reg_chrome.test(navigator.userAgent));
        };
        const { viewportWidth } = getViewportDimensions();
        if (viewportWidth <= 450 && isUnwantedBrowser()) {
            this.globalTargeting.addValues('bf', ['inskin']);
        }
    }

    @onPhase({ phase: Phase.TARGETING })
    public async checkTagType(): Promise<void> {
        const { useSingleRequest } = this.configResolver.get(selectAdserverGamSettings())?.config || {};
        this.globalTargeting.addValues('tagtype', useSingleRequest ? ['async', 'sra'] : ['async']);
    }

    @onPhase({ phase: Phase.TARGETING })
    public async collectConsentStatus(): Promise<void> {
        const tcData: TCData | undefined = this.consentCache.getTcData();
        const hasPurposeConsent = (id: number): boolean => !!tcData?.purpose?.consents?.[id];
        const hasVendorConsent = (id: number): boolean => !!tcData?.vendor?.consents?.[id];
        const hasVendorLi = (id: number): boolean => !!tcData?.vendor?.legitimateInterests?.[id];

        const csStatus: boolean = [1, 2, 3, 4, 7, 9, 10].every(hasPurposeConsent) && [755, 136, 137, 251].every(hasVendorConsent);
        this.globalTargeting.setValues('cs', [csStatus ? '1' : '0']);

        if (!hasVendorConsent(755) && hasVendorLi(755)) {
            this.globalTargeting.setValues('lta', ['1']);
        }
    }

    @onPhase({ phase: Phase.TRACKING })
    public async fireSimplePixels(): Promise<void> {
        const tcData: TCData | undefined = await this.consentCache.getTcDataPromise();

        this.configResolver.get(selectTargetingPixels()).forEach(([targetingName, targetingSettings]) => {
            const { active = false, consentRequired = true, iabVendorId = 0, url, pixelMedia: media, insertionQuery, oncePerSession, sampleRate } = targetingSettings;

            if (!active) return;

            // serve pixel only to a certain percentage of users
            if (isNumber(sampleRate) && sampleRate >= 0) {
                // possible values: 0 <= randomNumber < 100
                const randomNumber = 100 * Math.random();
                // If the random number is greater than the sample rate, the function exits early and the pixel is not served.
                // For example, if sampleRate is 10, only users for whom the random number is 0 through 10 (10% of users) will proceed and have the pixel served.
                if (randomNumber >= sampleRate) return;
            }

            // some pixels should only be fired once per session, avoiding unneeded requests
            if (oncePerSession) {
                const sessionStorageAllowed = evaluateConsent(tcData, [
                    { consentedVendors: [1057], consentedPurposes: [1] },
                    { gdprApplies: false }
                ]);
                if (sessionStorageAllowed) {
                    const storageKey = `sdgPixel_${targetingName}`;
                    if (sessionStorage.getItem(storageKey) === '1') return; // was already loaded in this session
                    sessionStorage.setItem(storageKey, '1');
                }
            }

            const allowed = !consentRequired || evaluateConsent(tcData, [
                { consentedVendors: [iabVendorId] },
                { legitimateInterestVendors: [iabVendorId] },
                { gdprApplies: false }
            ]);
            if (!allowed) return;

            const node = document.querySelector(insertionQuery) || document.querySelector('head')!;

            const src = url
                .replace('#{TIMESTAMP}', String(Math.random()).substring(2, 10))
                .replace('#{GDPR}', tcData?.gdprApplies ? '1' : '0')
                .replace('#{GDPR_CONSENT}', tcData?.tcString || '');

            if (media === 'script') {
                loadScript({ src, node });
                log.notice('script %s with url %s added successfully to website. ', [targetingName, url]);
            }
            if (media === 'img') {
                new Image().src = src;
                log.notice('pixel %s with url %s added successfully to website. ', [targetingName, url]);
            }
        });
    }

    /**
     * Will look for url parameters "sdmad" or "sdgkv" and their respective values.
     *
     * @example ?sdgkv=demo=uap
     */
    @onPhase({ phase: Phase.TARGETING })
    public collectUrlParameters(): void {
        // decodeURIComponent to decode sdmad=demo%3Duap to sdmad=demo=uap - https://stroeerdigitalgroup.atlassian.net/browse/METATAG-387
        const matches = decodeURIComponent(window.location.href).matchAll(/(?:(sdmad|sdgkv))=([a-zA-z-_0-9~]+)=([a-zA-z-_0-9~]+)/g);
        for (const [_match, _name, key, value] of matches) {
            this.globalTargeting.setValues(key, [value]);
        }
    }

    /**
     * Collects viewportDimensions and reports them as keyValue.
     */
    @onPhase({ phase: Phase.TARGETING })
    public collectViewportDimensions(): void {
        const { viewportWidth, viewportHeight } = getViewportDimensions();

        const widthSteps = [1, 240, 257, 321, 360, 375, 415, 433, 481, 641, 721, 848, 865, 965, 1153, 1280, 1321, 1341, 1381, 1421, 1441, 1461, 1501, 1541, 1581, 1651, 1681, 1801, 1901, 1921, 2049, 2801, 3841, 1e4];
        const heightSteps = [1, 501, 720, 769, 801, 901, 1025, 1081, 1153, 1201, 1441, 1601, 2161, 1e4];

        const widthRange = toRanges(widthSteps).find(selectRange(viewportWidth))?.join('-') ?? '';
        const heightRange = toRanges(heightSteps).find(selectRange(viewportHeight))?.join('-') ?? '';

        this.globalTargeting.setKeyValues({
            viewportWidth: [widthRange],
            viewportHeight: [heightRange],
        });
    }

    /**
     * gets users browser and the version
     */
    @onPhase({ phase: Phase.TARGETING })
    public collectBrowserData(): void {
        const { isTablet, isPhone, browserName, browserVersion } = getBrowserDetails();
        // set sr only if not already set by website; t-online is setting the sr key on their own
        if (!this.globalTargeting.has('sr')) {
            this.globalTargeting.setValues('sr', [(isTablet ? 'tab' : isPhone ? 'mew' : 'web')]);
        }
        // set browserapp and browserversion only if enabled via config
        if (this.settings.collectBrowserData) {
            this.globalTargeting.setKeyValues({
                browserapp: [browserName.toLowerCase()],
                browserversion: [browserVersion.toString()],
            });
        }
    }

    /**
     * Checks if the localStorage contains a specific key. If yes, check for another key containing a timestamp.
     * Calculate if the timestamp was set in the last 8 hours. If yes, will block interstitials via keyvalues, if no will delete both keys
     */
    @onPhase({ phase: Phase.TARGETING })
    public async collectGlobalFc(): Promise<void> {
        const interstitialFc = await this.storageHelper.get(StorageType.LOCAL, StorageConsent.IF_AVAILABLE_OR_DEFAULT, 'sdgInterstitialBlocker');

        if (interstitialFc) {
            const timestamp = await this.storageHelper.get(StorageType.LOCAL, StorageConsent.IF_AVAILABLE_OR_DEFAULT, 'sdgInterstitialBlockerTimestamp');

            if (timestamp && (Date.now() - parseFloat(timestamp)) < 14400000) {
                this.globalTargeting.addValues('globalfc', ['interstitial']);
            } else {
                //TODO why 2? can we combine them?
                this.storageHelper.delete(StorageType.LOCAL, StorageConsent.WAIT, 'sdgInterstitialBlocker');
                this.storageHelper.delete(StorageType.LOCAL, StorageConsent.WAIT, 'sdgInterstitialBlockerTimestamp');
            }
        }
    }

    /**
     * does the impression came via google, if yes send an keyvalue to the adserver indicating not to deliver any ads layering over the content
     * otherwise bad for google rankings
     */
    @onPhase({ phase: Phase.TARGETING })
    public detectGoogleReferrer(): void {
        if (this.settings.detectGoogleReferrer && /google./.test(document.referrer)) {
            this.globalTargeting.setValues('googref', ['1']);
        }
    }

    /**
     * synchronize sessionId with session storage
     *
     * TODO: this can be simplified when consent and storage is provided as observable
     */
    @onPhase({ phase: Phase.TARGETING })
    public async sessionId(): Promise<void> {
        const sessionIdKey = 'sdgSessionId';
        // when we have consent, read the sessionId
        this.storageHelper.get(StorageType.SESSION, StorageConsent.WAIT, sessionIdKey).then(value => {
            value && sessionId$.value !== value && sessionId$.next(value);
        });
        // sync back the sessionId to localStorage when there is consent
        sessionId$.subscribe(value => {
            this.storageHelper.set(StorageType.SESSION, StorageConsent.WAIT, sessionIdKey, value);
            this.globalTargeting.setValues('sessionId', [value]); // do we need the targeting?
        });
    }
}
